import {
  AccountCircleOutlined,
  DashboardOutlined,
  FastRewindOutlined,
  FolderOutlined,
} from "@mui/icons-material";
import {
  Box,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import React, { ReactElement, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Logo from "../assets/icons/logo2x.png";
import Button from "../components/buttons/Button";
import { Breadcrumbs } from "../components/misc/Breadcrumbs";
import Dialog from "../components/misc/Dialog";
import { useApi } from "../contexts/ApiContext";
import { useAppContext } from "../contexts/AppContext";
import { useAuth } from "../contexts/AuthContext";
import { useCase } from "../contexts/CaseContext";
import { logout } from "../firebase/firebaseAuth";
import Translations from "../lang/en/pages.json";
import { Page } from "../pages/cases/types";
import { generateRoute } from "../utils";
import { useTheme } from "styled-components";

const AppBar: React.FC<{ fixedTopBar: boolean }> = ({ fixedTopBar }) => {
  const { user } = useAuth();
  const { selectedPage, setSelectedPage } = useAppContext();
  const navigate = useNavigate();
  const { id: caseId, revisionId } = useParams();
  const { revisionMeta } = useCase() || {};
  const { client } = useApi();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // true on <600px

  console.info("isXs:", isXs);

  const pages: {
    page: Page;
    icon: ReactElement;
    path: string;
    disabled: boolean;
  }[] = [
    {
      page: "Dashboard",
      icon: <DashboardOutlined />,
      path: "dashboard/overview",
      disabled: !!revisionId || revisionMeta?.status !== "ready",
    },
    {
      page: "Documents",
      icon: <FolderOutlined />,
      path: "files",
      disabled:
        !!revisionId ||
        !["ready", "error"].includes(revisionMeta?.status ?? ""),
    },
    {
      page: "Revisions",
      icon: <FastRewindOutlined />,
      path: "revisions",
      disabled: !!revisionId || revisionMeta?.status !== "ready",
    },
  ];

  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [logoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const profileMenuOpen = Boolean(profileAnchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLogoutClick = () => {
    setLogoutConfirmationOpen(true);
  };

  const handleLogout = async () => {
    await client.POST("/v1/auth/logout");
    await logout();

    window.location.href = "/auth/login";
  };

  useEffect(() => {
    const urlSplits = window.location.pathname.split("/");
    let page = "dashboard";
    page = urlSplits[urlSplits.length - 1];
    page = ["dashboard", "files", "revisions"].includes(page)
      ? page
      : "dashboard";
    setSelectedPage(page as Page);
  }, []);

  const handleProfileClick = () => {
    navigate("/user");
  };

  return (
    <MuiAppBar
      sx={{
        margin: 0,
        paddingX: 8,
        paddingY: 1,
        boxShadow: "none",
        backgroundColor: "white",
      }}
      position={fixedTopBar ? "fixed" : "static"}
      id="minibar"
    >
      <Stack
        direction={"row"}
        spacing={1}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Button
          width={"auto"}
          onClick={() => navigate("/cases")}
          variant={"text"}
          sx={{ p: 0 }}
        >
          <img height={25} src={Logo} />
        </Button>

        {user && caseId && (
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            {pages.map((page, index) => (
              <Tooltip title={isXs ? page.page : ""} key={index}>
                <Box component="span" sx={{ display: "inline-block" }}>
                  <Button
                    disabled={page.disabled}
                    key={index}
                    variant={"topnav"}
                    className={page.path === selectedPage ? "selected" : ""}
                    onClick={() =>
                      navigate(generateRoute(caseId, page.path, revisionId))
                    }
                  >
                    <Stack
                      direction={"row"}
                      spacing={1}
                      alignItems={"center"}
                      whiteSpace={"nowrap"}
                    >
                      {page.icon}
                      <Typography
                        variant="body1"
                        sx={{ ml: 1, display: isXs ? "none" : "block" }}
                      >
                        {page.page}
                      </Typography>
                    </Stack>
                  </Button>
                </Box>
              </Tooltip>
            ))}
          </Stack>
        )}
        {user && (
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <IconButton
              onClick={handleClick}
              size="small"
              aria-controls={profileMenuOpen ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={profileMenuOpen ? "true" : undefined}
            >
              <AccountCircleOutlined fontSize={"small"} />
            </IconButton>
            <Menu
              id="account-menu"
              anchorEl={profileAnchorEl}
              open={profileMenuOpen}
              onClose={handleClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={handleProfileClick}>
                {Translations.pages.top_bar.profile.label}
              </MenuItem>
              <MenuItem onClick={handleLogoutClick}>
                {Translations.pages.top_bar.logout.label}
              </MenuItem>
            </Menu>
          </Stack>
        )}
      </Stack>

      <Breadcrumbs />

      <Dialog
        open={logoutConfirmationOpen}
        title={""}
        showClose={false}
        minHeight={"none"}
        onClose={() => setLogoutConfirmationOpen(false)}
      >
        <Stack spacing={2}>
          <Typography variant="body1">
            {Translations.pages.top_bar.logout.confirmation}
          </Typography>
          <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
            <Button
              onClick={() => setLogoutConfirmationOpen(false)}
              width={"auto"}
              variant={"grey"}
              color={"secondary"}
            >
              {Translations.pages.top_bar.logout.negative}
            </Button>
            <Button width={"auto"} onClick={handleLogout} variant={"green"}>
              {Translations.pages.top_bar.logout.positive}
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </MuiAppBar>
  );
};

export default AppBar;
